import { useEffect, useMemo, useState } from 'react'
import { MapPin, MapPinned, Users, UserPlus, Building2, Home } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Input } from '@/components/ui/Input'
import { Badge } from '@/components/ui/Badge'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import { type PageSize } from '@/hooks/useCrudList'
import type { PaginationMeta } from '@/types/api'
import { normalizeForMatch } from '@/lib/validators'
import { toast } from '@/components/ui/Toast'
import { SubscribersService, type LocationRow, type LocationTotals } from '@/services/modules/SubscribersService'
import { LookupService, type State } from '@/services/modules/LookupService'
import { BrazilMap } from '@/pages/Credentialing/BrazilMap'

const EMPTY_TOTALS: LocationTotals = {
  holders: 0, dependents: 0, companyHolders: 0, individualHolders: 0, companies: 0, total: 0,
}

export function ReportsLocationPage() {
  const [states, setStates] = useState<State[]>([])
  const [stateCounts, setStateCounts] = useState<Record<string, number>>({})
  const [globalTotals, setGlobalTotals] = useState<LocationTotals>(EMPTY_TOTALS)
  const [loadingMap, setLoadingMap] = useState(false)

  const [selectedUf, setSelectedUf] = useState<string | null>(null)
  const [selectedStateName, setSelectedStateName] = useState('')
  const [cityRows, setCityRows] = useState<LocationRow[]>([])
  const [stateTotals, setStateTotals] = useState<LocationTotals>(EMPTY_TOTALS)
  const [loadingCities, setLoadingCities] = useState(false)

  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<PageSize>(25)

  // Carga inicial: estados + agregação por estado (total de assinantes p/ tooltip do mapa).
  useEffect(() => {
    LookupService.states().then(setStates).catch(() => setStates([]))
    setLoadingMap(true)
    SubscribersService.reportLocation()
      .then((res) => {
        const counts: Record<string, number> = {}
        for (const r of res.data) if (r.abbreviation) counts[r.abbreviation] = r.total
        setStateCounts(counts)
        setGlobalTotals(res.totals)
      })
      .catch(() => toast.error('Erro ao carregar o mapa.'))
      .finally(() => setLoadingMap(false))
  }, [])

  function onMapSelect(uf: string) {
    const st = states.find((s) => s.abbreviation === uf)
    if (!st) { toast.info({ title: 'Estado sem cadastro', description: `Não há o estado "${uf}" cadastrado.` }); return }
    setSelectedUf(uf)
    setSelectedStateName(`${st.state} (${uf})`)
    setSearch(''); setPage(1)
    setLoadingCities(true)
    SubscribersService.reportLocation(st.id)
      .then((res) => { setCityRows(res.data); setStateTotals(res.totals) })
      .catch(() => toast.error('Erro ao carregar as cidades.'))
      .finally(() => setLoadingCities(false))
  }

  const filtered = useMemo(() => {
    const q = normalizeForMatch(search)
    if (!q) return cityRows
    return cityRows.filter((x) => normalizeForMatch(x.name).includes(q))
  }, [cityRows, search])

  const lastPage = Math.max(1, Math.ceil(filtered.length / pageSize))
  const safePage = Math.min(page, lastPage)
  const paged = filtered.slice((safePage - 1) * pageSize, safePage * pageSize)
  const meta: PaginationMeta = {
    current_page: safePage, per_page: pageSize, last_page: lastPage, total: filtered.length,
    from: filtered.length ? (safePage - 1) * pageSize + 1 : null,
    to: Math.min(safePage * pageSize, filtered.length) || null,
  }

  const columns: DataTableColumn<LocationRow>[] = [
    { key: 'name', header: 'Cidade', alwaysVisible: true, render: (r) => (
      <span className="inline-flex items-center gap-1.5 font-medium text-gray-800">
        <MapPin size={14} className="text-brand-500" />{r.name}
      </span>
    ) },
    { key: 'holders', header: 'Titulares', align: 'center', className: 'w-28', render: (r) => <Badge variant="blue">{r.holders}</Badge> },
    { key: 'dependents', header: 'Dependentes', align: 'center', className: 'w-32', render: (r) => <span className="text-gray-600">{r.dependents}</span> },
    { key: 'companyHolders', header: 'Tit. c/ empresa', align: 'center', className: 'w-36', render: (r) => <span className="text-gray-600">{r.companyHolders}</span> },
    { key: 'individualHolders', header: 'Tit. individuais', align: 'center', className: 'w-36', render: (r) => <span className="text-gray-600">{r.individualHolders}</span> },
    { key: 'companies', header: 'Empresas', align: 'center', className: 'w-28', render: (r) => <span className="text-gray-600">{r.companies}</span> },
    { key: 'total', header: 'Total', align: 'center', className: 'w-24', render: (r) => <span className="font-medium text-gray-800">{r.total}</span> },
  ]

  const exp = useDataTableExport<LocationRow>({
    filename: `assinantes-localidade-${selectedUf ?? 'estado'}`,
    service: { list: async () => ({ data: filtered }) },
    columns: [
      { header: 'Cidade', accessor: (r) => r.name },
      { header: 'Titulares', accessor: (r) => String(r.holders) },
      { header: 'Dependentes', accessor: (r) => String(r.dependents) },
      { header: 'Titulares c/ empresa', accessor: (r) => String(r.companyHolders) },
      { header: 'Titulares individuais', accessor: (r) => String(r.individualHolders) },
      { header: 'Empresas', accessor: (r) => String(r.companies) },
      { header: 'Total', accessor: (r) => String(r.total) },
    ],
  })

  const totalCountry = Object.values(stateCounts).reduce((a, b) => a + b, 0)

  // Bignumbers: quando um estado está selecionado mostra o estado; senão, o Brasil todo.
  const t = selectedUf ? stateTotals : globalTotals
  const bignumbers = [
    { label: 'Titulares', value: t.holders, icon: Users, color: 'text-brand-600', ring: 'ring-brand-200', bg: 'bg-brand-50' },
    { label: 'Dependentes', value: t.dependents, icon: UserPlus, color: 'text-violet-600', ring: 'ring-violet-200', bg: 'bg-violet-50' },
    { label: 'Empresas', value: t.companies, icon: Building2, color: 'text-emerald-600', ring: 'ring-emerald-200', bg: 'bg-emerald-50' },
    { label: 'Titulares c/ empresa', value: t.companyHolders, icon: Building2, color: 'text-amber-600', ring: 'ring-amber-200', bg: 'bg-amber-50' },
    { label: 'Titulares individuais', value: t.individualHolders, icon: Home, color: 'text-sky-600', ring: 'ring-sky-200', bg: 'bg-sky-50' },
  ]

  return (
    <>
      <PageHeader
        title="Assinantes por Localidade"
        subtitle="Clique em um estado para ver os assinantes ativos por cidade"
        crumbs={[{ label: 'Comercial' }, { label: 'Relatórios' }, { label: 'Localidade' }]}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        {/* Mapa */}
        <div className="rounded-md bg-white p-4 ring-1 ring-gray-200 sm:p-5 lg:col-span-3">
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
              <MapPinned size={16} className="text-brand-500" /> Brasil
            </div>
            <span className="text-xs text-gray-500">{totalCountry} assinante(s) ativo(s)</span>
          </div>
          {loadingMap
            ? <div className="grid h-80 place-items-center text-sm text-gray-400">Carregando mapa</div>
            : <BrazilMap counts={stateCounts} selected={selectedUf} onSelect={onMapSelect} />}
        </div>

        {/* Indicadores (Brasil ou estado selecionado) */}
        <div className="lg:col-span-2">
          <div className="mb-3 flex items-center gap-2">
            <MapPinned size={16} className="text-brand-500" />
            <h3 className="text-sm font-semibold text-gray-800">{selectedUf ? selectedStateName : 'Brasil (todos)'}</h3>
          </div>
          <div className="flex flex-col gap-3">
            {bignumbers.map((b) => (
              <div key={b.label} className={`flex items-center justify-between rounded-md p-4 ring-1 ${b.ring} ${b.bg}`}>
                <div>
                  <span className="text-[11px] font-medium leading-tight text-gray-500">{b.label}</span>
                  <div className={`mt-1 text-2xl font-bold ${b.color}`}>{(loadingCities && selectedUf) ? '—' : b.value}</div>
                </div>
                <b.icon size={20} className={b.color} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabela de cidades do estado selecionado */}
      {selectedUf && (
        <div className="mt-4 rounded-md bg-white p-4 ring-1 ring-gray-200 sm:p-5">
          <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
            <h3 className="text-sm font-semibold text-gray-800">Cidades de {selectedStateName}</h3>
            <Input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1) }}
              placeholder="Buscar cidade na lista"
              iconLeft={<MapPin size={14} />}
              clearable
              wrapperClassName="w-64"
            />
          </div>

          <DataTable
            tableId="reports.location"
            columns={columns}
            rows={paged}
            loading={loadingCities}
            getRowKey={(r) => r.id}
            emptyMessage="Nenhum assinante ativo neste estado."
            exportable
            onExportPage={() => exp.exportPage(paged)}
            onExportAll={exp.exportAll}
          />
          <Pagination meta={meta} size={pageSize} onChange={setPage} onSizeChange={setPageSize} />
        </div>
      )}
    </>
  )
}

export default ReportsLocationPage
